import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, getDoc, Timestamp } from 'firebase/firestore';

const IMAGE_PRESETS_COLLECTION = 'imagePresets';

/**
 * Image Presets Service
 * Manages named image settings profiles per user.
 * Each preset stores all the BulkEditImages settings (format, compression, EXIF, etc.)
 * so users can quickly switch between configurations and use them in workflow automation.
 */

/**
 * Get all image presets for a user
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} Array of preset objects
 */
export const getImagePresets = async (userId) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const presetsRef = collection(db, IMAGE_PRESETS_COLLECTION);
    const q = query(presetsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);

    const presets = [];
    querySnapshot.forEach((docSnap) => {
      presets.push({
        id: docSnap.id,
        ...docSnap.data()
      });
    });

    // Sort by name alphabetically
    presets.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    return presets;
  } catch (error) {
    console.error('Error loading image presets:', error);
    throw error;
  }
};

/**
 * Get a single image preset by ID
 * @param {string} presetId - The preset document ID
 * @returns {Promise<Object|null>} Preset object or null if not found
 */
export const getImagePreset = async (presetId) => {
  try {
    if (!presetId) throw new Error('Preset ID is required');

    const presetRef = doc(db, IMAGE_PRESETS_COLLECTION, presetId);
    const presetDoc = await getDoc(presetRef);

    if (!presetDoc.exists()) {
      return null;
    }

    return {
      id: presetDoc.id,
      ...presetDoc.data()
    };
  } catch (error) {
    console.error('Error getting image preset:', error);
    throw error;
  }
};

/**
 * Save a new image preset
 * @param {string} userId - The user ID
 * @param {Object} presetData - Preset data including name and all image settings
 * @returns {Promise<Object>} The created preset with ID
 */
export const saveImagePreset = async (userId, presetData) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!presetData) throw new Error('Preset data is required');
    if (!presetData.name?.trim()) throw new Error('Preset name is required');

    const newPreset = {
      userId,
      name: presetData.name.trim(),
      // Image format & compression
      format: presetData.format || 'none',
      compressionLevel: presetData.compressionLevel || 'medium',
      // Dimensions
      maxWidth: presetData.maxWidth || null,
      maxHeight: presetData.maxHeight || null,
      // SEO
      useSeoNaming: Boolean(presetData.useSeoNaming),
      // EXIF Metadata
      artist: presetData.artist || '',
      comment: presetData.comment || '',
      copyright: presetData.copyright || '',
      imageDescription: presetData.imageDescription || '',
      // Timestamps
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, IMAGE_PRESETS_COLLECTION), newPreset);

    return {
      id: docRef.id,
      ...newPreset
    };
  } catch (error) {
    console.error('Error saving image preset:', error);
    throw error;
  }
};

/**
 * Update an existing image preset
 * @param {string} presetId - The preset document ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} The updated preset
 */
export const updateImagePreset = async (presetId, updates) => {
  try {
    if (!presetId) throw new Error('Preset ID is required');
    if (!updates) throw new Error('Update data is required');

    const presetRef = doc(db, IMAGE_PRESETS_COLLECTION, presetId);
    const presetDoc = await getDoc(presetRef);

    if (!presetDoc.exists()) {
      throw new Error('Image preset not found');
    }

    const updateData = {
      ...(updates.name !== undefined && { name: updates.name.trim() }),
      ...(updates.format !== undefined && { format: updates.format }),
      ...(updates.compressionLevel !== undefined && { compressionLevel: updates.compressionLevel }),
      ...(updates.maxWidth !== undefined && { maxWidth: updates.maxWidth }),
      ...(updates.maxHeight !== undefined && { maxHeight: updates.maxHeight }),
      ...(updates.useSeoNaming !== undefined && { useSeoNaming: Boolean(updates.useSeoNaming) }),
      ...(updates.artist !== undefined && { artist: updates.artist }),
      ...(updates.comment !== undefined && { comment: updates.comment }),
      ...(updates.copyright !== undefined && { copyright: updates.copyright }),
      ...(updates.imageDescription !== undefined && { imageDescription: updates.imageDescription }),
      updatedAt: new Date().toISOString()
    };

    await updateDoc(presetRef, updateData);

    return {
      id: presetId,
      ...presetDoc.data(),
      ...updateData
    };
  } catch (error) {
    console.error('Error updating image preset:', error);
    throw error;
  }
};

/**
 * Delete an image preset
 * @param {string} presetId - The preset document ID
 * @returns {Promise<void>}
 */
export const deleteImagePreset = async (presetId) => {
  try {
    if (!presetId) throw new Error('Preset ID is required');

    const presetRef = doc(db, IMAGE_PRESETS_COLLECTION, presetId);
    await deleteDoc(presetRef);
  } catch (error) {
    console.error('Error deleting image preset:', error);
    throw error;
  }
};

/**
 * Build a default settings object (used when no preset is selected)
 */
export const getDefaultImageSettings = () => ({
  format: 'none',
  compressionLevel: 'medium',
  maxWidth: null,
  maxHeight: null,
  useSeoNaming: true,
  artist: '',
  comment: '',
  copyright: '',
  imageDescription: ''
});
